import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BookingCalendar from "@/components/BookingCalendar/BookingCalendar";
import useDismiss from "@/hooks/useDismiss";
import isoDate from "@/utils/isoDate";
import "@/components/StayDatesPicker/StayDatesPicker.scss";

type DateField = "checkIn" | "checkOut";

interface StayDatesPickerProps {
  /** ISO dates, "" when unset. */
  checkIn: string;
  checkOut: string;
  onChange: (checkIn: string, checkOut: string) => void;
  /** Applied to both fields, so they look like the surrounding form's fields. */
  fieldClassName?: string;
}

/**
 * Check-in and check-out side by side, opening one shared two-month calendar (like Airbnb): pick the
 * check-in day, then the check-out day; it closes once both are set.
 */
function StayDatesPicker({ checkIn, checkOut, onChange, fieldClassName }: StayDatesPickerProps) {
  const { t, i18n } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [activeField, setActiveField] = useState<DateField | null>(null);

  const close = useCallback(() => setActiveField(null), []);
  useDismiss(rootRef, activeField !== null, close);

  const checkInDate = checkIn ? isoDate.parseIsoDate(checkIn) : null;
  const checkOutDate = checkOut ? isoDate.parseIsoDate(checkOut) : null;

  const shortDate = new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" });
  const display = (date: Date | null) => (date ? shortDate.format(date) : t("search.addDates"));

  const open = (field: DateField) => {
    if (field === "checkOut" && checkInDate) {
      // Re-pick the check-out day: the calendar asks for it while check-out is unset.
      onChange(checkIn, "");
      setActiveField("checkOut");
      return;
    }
    setActiveField("checkIn");
  };

  const handleCalendarChange = (nextCheckIn: Date | null, nextCheckOut: Date | null) => {
    onChange(
      nextCheckIn ? isoDate.toIsoDate(nextCheckIn) : "",
      nextCheckOut ? isoDate.toIsoDate(nextCheckOut) : ""
    );
    if (nextCheckIn && nextCheckOut) {
      close();
    } else {
      setActiveField(nextCheckIn ? "checkOut" : "checkIn");
    }
  };

  const fieldClass = (field: DateField) =>
    [
      fieldClassName,
      "stay-dates__field",
      activeField === field ? "stay-dates__field--active" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const renderField = (field: DateField, date: Date | null) => (
    <div className={fieldClass(field)}>
      <label id={`${field}-label`} htmlFor={field}>
        {t(field === "checkIn" ? "search.checkInLabel" : "search.checkOutLabel")}
      </label>
      <button
        id={field}
        type="button"
        className="stay-dates__toggle"
        aria-haspopup="dialog"
        aria-expanded={activeField !== null}
        aria-labelledby={`${field}-label ${field}-value`}
        onClick={() => (activeField === field ? close() : open(field))}
      >
        <span
          id={`${field}-value`}
          className={date ? undefined : "stay-dates__placeholder"}
        >
          {display(date)}
        </span>
      </button>
    </div>
  );

  return (
    <div ref={rootRef} className="stay-dates">
      {renderField("checkIn", checkInDate)}
      {renderField("checkOut", checkOutDate)}

      {activeField !== null && (
        <div className="stay-dates__panel" role="dialog" aria-label={t("search.datesDialog")}>
          <BookingCalendar
            bookedRanges={[]}
            checkIn={checkInDate}
            checkOut={checkOutDate}
            onChange={handleCalendarChange}
            months={2}
            showLegend={false}
          />
          <div className="stay-dates__footer">
            <button
              type="button"
              className="stay-dates__clear"
              disabled={!checkIn && !checkOut}
              onClick={() => {
                onChange("", "");
                setActiveField("checkIn");
              }}
            >
              {t("search.clearDates")}
            </button>
            <button type="button" className="stay-dates__close" onClick={close}>
              {t("search.closeDates")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StayDatesPicker;
export type { StayDatesPickerProps };
